import { Check } from 'lucide-react';
import type { Point } from '../content/pages.js';
import { CONTAINER } from '../lib/layout.js';
import { Reveal } from './Reveal.js';

/**
 * The three shapes the standalone pages are built from.
 *
 * `HowItWorksPage` established them — a rule above each item, display type
 * for the term, muted prose under it — and every page added since repeats
 * the pattern. They are lifted here so that eight pages share one set
 * rather than eight near-copies that drift apart the first time a heading
 * size changes.
 */

export interface BandProps {
  heading: string;
  /** Sits under the heading, capped to a readable measure. */
  lead?: string;
  children: React.ReactNode;
  /** Sunken with rules top and bottom, for alternating bands. */
  sunken?: boolean;
}

export function Band({ heading, lead, children, sunken = false }: BandProps) {
  return (
    <section className={sunken ? 'border-y border-border-default bg-surface-sunken' : 'bg-surface'}>
      <div className={`${CONTAINER} py-20`}>
        <h2 className="font-display text-3xl font-bold tracking-tight md:text-4xl">{heading}</h2>
        {lead ? <p className="mt-3 max-w-xl leading-relaxed text-text-muted">{lead}</p> : null}
        <div className="mt-10">{children}</div>
      </div>
    </section>
  );
}

/** A row of terms over rules — the shape `locations.delivery` uses. */
export function PointGrid({
  points,
  columns = 3,
}: {
  points: readonly Point[];
  columns?: 2 | 3 | 4;
}) {
  // Spelled out because Tailwind reads class names out of the source and
  // cannot see a template string.
  const grid = {
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-2 lg:grid-cols-3',
    4: 'sm:grid-cols-2 lg:grid-cols-4',
  }[columns];

  return (
    <Reveal stagger className={`grid gap-x-10 gap-y-8 ${grid}`}>
      {points.map((point) => (
        <div key={point.title} className="border-t border-border-strong pt-5">
          <h3 className="font-display text-lg font-bold">{point.title}</h3>
          <p className="mt-1.5 leading-relaxed text-text-muted">{point.body}</p>
        </div>
      ))}
    </Reveal>
  );
}

/** Numbered steps, for the pages that describe a sequence. */
export function StepList({ steps }: { steps: readonly Point[] }) {
  return (
    <Reveal stagger className="grid gap-x-10 gap-y-8 sm:grid-cols-2 lg:grid-cols-4">
      {steps.map((step, index) => (
        <div key={step.title} className="border-t border-border-strong pt-5">
          <span className="font-display text-sm font-bold text-text-subtle">
            {String(index + 1).padStart(2, '0')}
          </span>
          <h3 className="mt-2 font-display text-lg font-bold">{step.title}</h3>
          <p className="mt-1.5 leading-relaxed text-text-muted">{step.body}</p>
        </div>
      ))}
    </Reveal>
  );
}

/** A ticked list, for the "what you get" blocks. */
export function CheckList({ items }: { items: readonly string[] }) {
  return (
    <ul className="space-y-4">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3.5">
          <span className="mt-0.5 grid size-6 shrink-0 place-items-center rounded-full bg-accent text-accent-text">
            <Check className="size-3.5 stroke-[3]" />
          </span>
          <span className="leading-relaxed">{item}</span>
        </li>
      ))}
    </ul>
  );
}

/** Two columns of question and answer, matching the how-it-works FAQ. */
export function Faq({ heading, items }: { heading: string; items: readonly Point[] }) {
  return (
    <section className="bg-surface">
      <div className={`${CONTAINER} py-20`}>
        <h2 className="font-display text-3xl font-bold tracking-tight md:text-4xl">{heading}</h2>
        <Reveal stagger className="mt-10 grid gap-x-16 gap-y-10 md:grid-cols-2">
          {items.map((item) => (
            <div key={item.title}>
              <h3 className="font-display text-lg font-bold">{item.title}</h3>
              <p className="mt-2 leading-relaxed text-text-muted">{item.body}</p>
            </div>
          ))}
        </Reveal>
      </div>
    </section>
  );
}

/** Running prose, capped to a measure somebody will actually finish. */
export function Prose({ paragraphs }: { paragraphs: readonly string[] }) {
  return (
    <div className="max-w-[68ch] space-y-4">
      {paragraphs.map((paragraph) => (
        <p key={paragraph} className="text-lg leading-relaxed text-text-muted">
          {paragraph}
        </p>
      ))}
    </div>
  );
}
